import { Box } from "arkh";
import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetWeather } from "widgets";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { getWidgetAddWidgetSizeWithinBounds } from "../utils/useWidgetAddWidgetSizeState";
import { WACategoryScreenProps } from "../WANavigation";
import { WACategoryWidgetContainer } from "../WACategoryWidgetContainer";
import { WACategoryWeatherQuery } from "./__generated__/WACategoryWeatherQuery.graphql";
import { weatherWidgetSizes } from "../utils/widgetSizes/weatherWidgetSizes";
import { FormattedMessage } from "react-intl";
import { useWACategoryLayout } from "../utils/useWACategoryLayout";

type Props = WACategoryScreenProps<"WACategoryWeather">;
export function WACategoryWeather(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWACategoryWeather {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryWeather(props: Props & { fetchKey: string }) {
  const intl_tokens = useIntlTokens();

  const data = useLazyLoadQuery<WACategoryWeatherQuery>(
    graphql`
      query WACategoryWeatherQuery(
        $storecastId: ID!
        $input: PreviewWidgetWeatherInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetWeather(input: $input) {
            ...WidgetWeather_widget
          }
        }
      }
    `,
    {
      input: {
        // palma
        lat: 39.571625,
        lng: 2.650544,
        size: "TWO_X_ONE",
      },
      storecastId: props.route.params.storecastId,
    },
    { fetchKey: props.fetchKey }
  );

  const [size, hasSizeWithinBounds] = useMemo(
    () =>
      getWidgetAddWidgetSizeWithinBounds({
        maxSize: props.route.params.maxSize,
        widgetSizes: weatherWidgetSizes,
      }),
    [props.route.params.maxSize]
  );

  const onAdd = useCallback(() => {
    props.navigation.navigateDeprecated("WidgetAddWeather", {
      defaultTeams: props.route.params.defaultTeams,
      position: props.route.params.position,
      storecastId: props.route.params.storecastId,
      unitSize: props.route.params.unitSize,
      maxSize: props.route.params.maxSize,
    });
  }, [
    props.navigation,
    props.route.params.defaultTeams,
    props.route.params.maxSize,
    props.route.params.position,
    props.route.params.storecastId,
    props.route.params.unitSize,
  ]);

  const paddingHorizontal = useWACategoryLayout().paddingHorizontal;

  return (
    <Box
      style={[
        {
          paddingHorizontal,
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        },
      ]}
    >
      <WACategoryWidgetContainer
        hasSizeWithinBounds={hasSizeWithinBounds}
        size={size}
        title={intl_tokens.Weather}
        unitSize={props.route.params.unitSize}
        onAdd={onAdd}
        addTitle={intl_tokens.SelectLocation}
        description={
          <FormattedMessage
            description="WA Weather Description"
            defaultMessage="Feature the weather forecast in order to stay prepared for weather based shopping patterns"
          />
        }
      >
        <WidgetWeather
          clientForceSize={
            size.w === 2 && size.h === 1 ? "TWO_X_ONE" : "ONE_X_ONE"
          }
          widget={data.dashboard.previewWidgetWeather}
          storecastId={props.route.params.storecastId}
          mode="IDLE"
        />
      </WACategoryWidgetContainer>
    </Box>
  );
}
